<template>
<v-card>
  <v-card-title>
    <span>Edit Website Project</span>
    <v-spacer></v-spacer>
    <v-checkbox v-model="sync" label="Sync"></v-checkbox>
  </v-card-title>
  <v-card-text>
    <v-container>
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-text-field v-model="title" label="Title" autocomplete="off"></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="client" label="Client" autocomplete="off"></v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="10">
          <v-autocomplete v-model="jobId" :items="jobsList" label="Job" clearable
                          item-value="id" item-text="text"></v-autocomplete>
        </v-col>
        <v-col cols="2">
          <v-text-field v-model.number="year" label="Year" type="number" autocomplete="off"></v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-text-field v-model="mainYoutubeId" label="Main YouTube ID"
                        @blur="mainYoutubeBlur" @keyup.enter="$event.target.blur()"></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="btsYoutubeId" label="BTS YouTube ID"
                        @blur="btsYoutubeBlur" @keyup.enter="$event.target.blur()"></v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="4">
          <v-autocomplete v-model="categories" :items="websiteMeta.categories" label="Categories"
                          multiple item-value="category"
                          :item-text="item => item.sector + ' → ' + item.category">
            <template v-slot:selection="{ item, on, attrs }">
              <v-chip v-bind="attrs" v-on="on" small close :color="item.colour" dark
                      @click:close="categories = categories.filter(c => c !== item.category)">
                {{ item.category }}
              </v-chip>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="8">
          <v-autocomplete v-model="effects" :items="websiteMeta.effects" label="Effects"
                          multiple item-value="effect"
                          :item-text="item => item.discipline + ' → ' + item.effect">
            <template v-slot:selection="{ item, on, attrs }">
              <v-chip v-bind="attrs" v-on="on" small close :color="item.colour" dark
                      @click:close="effects = effects.filter(e => e !== item.effect)">
                {{ item.effect }}
              </v-chip>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-combobox v-model="tags" :items="websiteMeta.tags" label="Tags"
                      multiple chips small-chips deletable-chips
                      :search-input.sync="tagSearch">
            <template v-slot:no-data>
              <v-list-item v-if="tagSearch">
                <v-list-item-content>
                  <v-list-item-title>
                    Press Enter to add "<strong>{{ tagSearch }}</strong>" as a new tag
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-combobox>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col v-for="key in requiredRoles" :key="key" cols="12" sm="4">
          <v-text-field v-model="roles[key]" :label="key" autocomplete="off"></v-text-field>
        </v-col>
      </v-row>
      <v-row dense v-for="key in visibleOptionalKeys" :key="'opt-' + key">
        <v-col cols="3">
          <v-text-field v-model="roles[key]" :label="key" autocomplete="off"></v-text-field>
        </v-col>
        <v-col cols="1" class="d-flex align-center justify-end">
          <v-icon small @click="removeOptionalRole(key)">mdi-close</v-icon>
        </v-col>
      </v-row>
      <v-row dense align="center">
        <v-col cols="6" sm="4">
          <v-select v-model="newRoleKey" :items="availableOptionalRoles" label="Add credit"
                    clearable @change="addOptionalRole"></v-select>
        </v-col>
        <template v-if="newRoleKey === 'Other'">
          <v-col cols="4">
            <v-text-field v-model="customRoleKey" label="Role name"
                          autocomplete="off" @keyup.enter="addCustomRole"></v-text-field>
          </v-col>
          <v-col cols="2" class="d-flex align-center">
            <v-btn x-small text color="primary" @click="addCustomRole">Add</v-btn>
          </v-col>
        </template>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-textarea v-model="description" label="Description" rows="3" auto-grow></v-textarea>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-text-field v-model="keywords" label="Keywords" rows="2" auto-grow></v-text-field>
        </v-col>
      </v-row>
      <v-row dense class="mt-3">
        <v-col cols="3">
          <v-text-field v-model.number="featured" label="Featured" type="number"
                        clearable hide-details autocomplete="off"
                        @click:clear="featured = null"></v-text-field>
        </v-col>
        <v-col cols="3">
          <v-checkbox v-model="inRelated" label="In related"></v-checkbox>
        </v-col>
      </v-row>
      <v-row dense class="mt-4">
        <v-col cols="12"><span class="subtitle-2">Images</span></v-col>
      </v-row>
      <v-row dense>
        <v-col cols="6" v-for="folder in ['thumbnail', 'featured']" :key="folder">
          <div class="image-folder-label text-capitalize">{{ folder }}</div>
          <div class="image-row">
            <img v-if="images[folder].length"
                 :src="imageUrl(folder, images[folder][0])"
                 class="project-image" :alt="images[folder][0]">
            <v-file-input :label="images[folder].length ? 'Replace' : 'Upload'"
                          accept="image/*" hide-details dense class="image-upload"
                          prepend-icon="mdi-image"
                          @change="uploadImage(folder, $event)">
            </v-file-input>
          </div>
        </v-col>
      </v-row>
      <v-row dense v-for="folder in ['main', 'bts']" :key="folder">
        <v-col cols="12">
          <div class="image-folder-label text-capitalize">{{ folder }}</div>
          <div class="image-row">
            <div v-for="filename in images[folder]" :key="filename" class="image-wrap">
              <img :src="imageUrl(folder, filename)" class="project-image" :alt="filename">
              <v-btn class="image-delete" icon x-small color="error"
                     @click="deleteImage(folder, filename)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>
            <v-file-input label="Add image" accept="image/*" class="image-upload"
                          hide-details dense prepend-icon="mdi-image-multiple"
                          @change="uploadImage(folder, $event)">
            </v-file-input>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-card-text>
  <v-card-actions>
    <v-spacer></v-spacer>
    <v-btn text @click="$emit('close')">Cancel</v-btn>
    <v-btn text color="primary" @click="save">Save</v-btn>
  </v-card-actions>
</v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import * as api from '../../api'
import { requiredRoles, optionalRoles } from '../../data'

const server = process.env.NODE_ENV === 'development'
  ? 'http://flossy.local:8001'
  : `https://${process.env.VUE_APP_STAGE === 'dev' ? 'dev.' : ''}api.timesheets.machineshop.co.uk`

export default {
  name: 'EditWebsiteProject',
  data() {
    return {
      projectId: null,
      title: '',
      client: '',
      year: null,
      jobId: null,
      mainYoutubeId: '',
      btsYoutubeId: '',
      categories: [],
      effects: [],
      tags: [],
      description: '',
      keywords: '',
      roles: {},
      visibleOptionalKeys: [],
      newRoleKey: null,
      customRoleKey: '',
      sync: false,
      featured: null,
      inRelated: false,
      tagSearch: '',
      requiredRoles,
      images: { thumbnail: [], featured: [], main: [], bts: [] }
    }
  },
  computed: {
    ...mapGetters(['jobsList']),
    ...mapState(['websiteMeta']),
    availableOptionalRoles() {
      return [...optionalRoles.filter(r => !this.visibleOptionalKeys.includes(r)), 'Other']
    }
  },
  watch: {
    tags(val) {
      const newTag = val.find(t => !this.websiteMeta.tags.includes(t))
      if (newTag) {
        api.post('/website/tag', { tag: newTag })
        this.$store.dispatch('getWebsiteMeta', true)
      }
    }
  },
  methods: {
    async setProject(project) {
      this.projectId = project.id
      this.title = project.title || ''
      this.client = project.client || ''
      this.year = project.year
      this.jobId = project.job || null
      this.mainYoutubeId = project.main_youtube_id || ''
      this.btsYoutubeId = project.bts_youtube_id || ''
      this.categories = project.categories || []
      this.effects = project.effects || []
      this.tags = project.tags || []
      this.description = project.description || ''
      this.keywords = project.keywords || ''
      this.sync = project.sync || false
      this.featured = project.featured ?? null
      this.inRelated = project.in_related || false
      this.roles = { ...(project.roles || {}) }
      for (const key of requiredRoles) {
        if (!(key in this.roles)) this.$set(this.roles, key, '')
      }
      this.visibleOptionalKeys = Object.keys(this.roles).filter(k => !requiredRoles.includes(k))
      this.newRoleKey = null
      this.customRoleKey = ''
      this.images = { thumbnail: [], featured: [], main: [], bts: [] }
      const imgData = await api.get(`/website/project/${project.id}/images`)
      console.log(imgData)
      if (imgData) this.images = imgData
    },
    extractYoutubeId(s) {
      const m = s.match(/https:\/\/www\.youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/)
      return m ? m[1] : s
    },
    mainYoutubeBlur(event) {
      this.mainYoutubeId = this.extractYoutubeId(event.target.value)
    },
    btsYoutubeBlur(event) {
      this.btsYoutubeId = this.extractYoutubeId(event.target.value)
    },
    addOptionalRole(key) {
      if (!key || key === 'Other') return
      if (!this.visibleOptionalKeys.includes(key)) {
        this.visibleOptionalKeys.push(key)
        if (!(key in this.roles)) this.$set(this.roles, key, '')
      }
      this.$nextTick(() => { this.newRoleKey = null })
    },
    addCustomRole() {
      const key = this.customRoleKey.trim()
      if (!key || this.visibleOptionalKeys.includes(key)) return
      this.visibleOptionalKeys.push(key)
      this.$set(this.roles, key, '')
      this.customRoleKey = ''
      this.newRoleKey = null
    },
    removeOptionalRole(key) {
      this.visibleOptionalKeys = this.visibleOptionalKeys.filter(k => k !== key)
      this.$delete(this.roles, key)
    },
    imageUrl(folder, filename) {
      return `${server}/website/project/${this.projectId}/image/${folder}/${filename}`
    },
    async uploadImage(folder, file) {
      if (!file) return
      await api.postFile(`/website/project/${this.projectId}/image`, folder, file)
      const imgData = await api.get(`/website/project/${this.projectId}/images`)
      if (imgData) this.images = imgData
    },
    async deleteImage(folder, filename) {
      await api.del(`/website/project/${this.projectId}/image/${folder}/${filename}`)
      const imgData = await api.get(`/website/project/${this.projectId}/images`)
      if (imgData) this.images = imgData
    },
    async save() {
      const rolesPayload = {}
      for (const key of [...requiredRoles, ...this.visibleOptionalKeys]) {
        const val = this.roles[key]
        if (val && val.trim()) rolesPayload[key] = val.trim()
      }
      const payload = {
        title: this.title,
        client: this.client,
        main_youtube_id: this.mainYoutubeId,
        bts_youtube_id: this.btsYoutubeId,
        categories: this.categories,
        effects: this.effects,
        tags: this.tags,
        description: this.description,
        keywords: this.keywords,
        roles_json: JSON.stringify(rolesPayload),
        sync: this.sync,
        featured: this.featured ?? null,
        in_related: this.inRelated
      }

      if (this.year) {
        payload.year = this.year
      }
      if (this.jobId) {
        payload.jobId = this.jobId
      }

      await api.put(`/website/project/${this.projectId}`, payload)
      this.$store.dispatch('getWebsiteProjects', true)
      this.$emit('close')
    }
  },
  created() {
    this.$store.dispatch('getJobs')
    this.$store.dispatch('getWebsiteMeta')
  }
}
</script>

<style scoped lang="scss">
.image-folder-label {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}
.image-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  .image-wrap {
    position: relative;
    .project-image {
      height: 80px;
      width: auto;
      border-radius: 4px;
      object-fit: cover;
      display: block;
    }
    .image-delete {
      position: absolute;
      top: -6px;
      right: -6px;
      background: white;
    }
  }
  .project-image {
    height: 80px;
    width: auto;
    border-radius: 4px;
    object-fit: cover;
  }
  .image-upload {
    max-width: 200px;
  }
}
</style>
